<template>
  <v-container fluid fill-height class="container-text pa-0" style="overflow: hidden; position: relative">
    <bubble-menu class="bubble-menu" :tippy-options="{ duration: 100 }" :editor="editor" v-if="editor">
      <button @click="editor.chain().focus().toggleBold().run()" :class="{ 'is-active': editor.isActive('bold') }">
        <i class="ri-bold"></i>
      </button>
      <button @click="editor.chain().focus().toggleItalic().run()" :class="{ 'is-active': editor.isActive('italic') }">
        <i class="ri-italic"></i>
      </button>
      <button
        @click="editor.chain().focus().toggleUnderline().run()"
        :class="{ 'is-active': editor.isActive('underline') }"
      >
        <i class="ri-underline"></i>
      </button>
      <button @click="editor.chain().focus().toggleStrike().run()" :class="{ 'is-active': editor.isActive('strike') }">
        <i class="ri-strikethrough"></i>
      </button>
      <button @click="editor.isActive('link') ? unsetLink() : openDialog()">
        <i v-if="editor.isActive('link')" class="ri-link-unlink"></i>
        <i v-else class="ri-link"></i>
      </button>
      <button class="bubble-button-color">
        <input type="color" @input="editor.chain().focus().setColor($event.target.value).run()" />
      </button>
    </bubble-menu>

    <editor-content :editor="editor" @keydown.space.native="spaceHack" spellcheck="false" />

    <v-card v-if="isModify" class="edit-btns">
      <v-btn
        @click="openDialog('Undo')"
        width="120"
        height="40"
        color="#96a7af"
        class="mr-2"
        style="color: #31434f"
        elevation="1"
      >
        {{ 'Undo' }}
      </v-btn>
      <v-btn
        @click="setClipTextUpdate"
        :loading="isSaveTextLoading"
        width="120"
        height="40"
        color="#289067"
        style="color: #eee"
        elevation="1"
      >
        {{ 'Save' }}
      </v-btn>
    </v-card>
    <v-dialog v-model="setLinkDialog.show" width="500">
      <v-card style="background-color: #21343b; padding: 30px; border-radius: 10px">
        <v-text-field
          dark
          class="add-url-input"
          placeholder="https://..."
          type="input"
          v-model="setLinkDialog.url"
          @keydown.enter="(e) => setLink(e.target.value)"
          solo
          background-color="#1a272e"
        ></v-text-field>
        <v-btn color="rgb(40, 144, 103)" width="100" block @click="setLink(setLinkDialog.url)" style="color: #eee">
          {{ 'Add Link' }}
        </v-btn>
      </v-card>
    </v-dialog>
    <texteditundo-dialog
      v-if="isTexteditundoDialog"
      @close="closeDialog"
      @undo="undoResult"
      :isTexteditundoDialog.sync="isTexteditundoDialog"
    ></texteditundo-dialog>
  </v-container>
</template>

<script>
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import { Editor, EditorContent, BubbleMenu, VueNodeViewRenderer } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import javascript from 'highlight.js/lib/languages/javascript'
import html from 'highlight.js/lib/languages/xml'
import css from 'highlight.js/lib/languages/css'
import python from 'highlight.js/lib/languages/python'
import sql from 'highlight.js/lib/languages/sql'
import typescript from 'highlight.js/lib/languages/typescript'
import csharp from 'highlight.js/lib/languages/csharp'
import bash from 'highlight.js/lib/languages/bash'
import shell from 'highlight.js/lib/languages/shell'
import java from 'highlight.js/lib/languages/java'
import cpp from 'highlight.js/lib/languages/cpp'
import c from 'highlight.js/lib/languages/c'
import CodeBlockComponent from './CodeBlockComponent.vue'
import { lowlight } from 'lowlight'
lowlight.registerLanguage('Javascript', javascript)
lowlight.registerLanguage('HTML', html)
lowlight.registerLanguage('CSS', css)
lowlight.registerLanguage('Python', python)
lowlight.registerLanguage('SQL', sql)
lowlight.registerLanguage('Java', java)
lowlight.registerLanguage('Typescript', typescript)
lowlight.registerLanguage('C#', csharp)
lowlight.registerLanguage('Bash', bash)
lowlight.registerLanguage('Shell', shell)
lowlight.registerLanguage('C++', cpp)
lowlight.registerLanguage('C', c)
import Commands from './commands'
import suggestion from './suggestion'
import { Color } from '@tiptap/extension-color'
import Underline from '@tiptap/extension-underline'
import TextStyle from '@tiptap/extension-text-style'
import Placeholder from '@tiptap/extension-placeholder'
import Link from '@tiptap/extension-link'
import { setClipTextUpdate } from '@/services/clipApi'
import { mapGetters, mapState } from 'vuex'
import Toast from '@/views/meeting/component/Toast'
import TexteditundoDialog from '@/views/modal/TexteditundoDialog'

export default {
  components: {
    EditorContent,
    BubbleMenu,
    TexteditundoDialog,
  },
  data() {
    return {
      isSaveTextLoading: false,
      editor: null,
      editorble: null,
      textClip: '',
      setLinkDialog: { url: '', show: false },
      isModify: false,
      isTexteditundoDialog: false,
      lastLine: [],
    }
  },
  mounted() {
    this.editor = new Editor({
      content: this.content,
      editable: this.isClipUploader ? true : false,
      scrollIntoView: 'false',
      parseOptions: {
        preserveWhitespace: 'full',
      },
      extensions: [
        StarterKit,
        Color.configure({
          types: ['textStyle'],
        }),
        Placeholder.configure({
          emptyEditorClass: 'is-editor-empty',
          placeholder: 'Type "/" for commands',
        }),
        Underline,
        TextStyle,
        Commands.configure({
          suggestion,
        }),
        CodeBlockLowlight.extend({
          addNodeView() {
            return VueNodeViewRenderer(CodeBlockComponent)
          },
        }).configure({
          lowlight,
          defaultLanguage: 'auto',
        }),
        Link.configure({
          // openOnClick: true,
          // autolink: true,
        }),
      ],
      onUpdate: () => {
        // HTML
        // this.$emit('input', this.editor.getHTML())
        // this.editor.setHTML('<p></p>')
        this.textClip = this.editor.getHTML()
        this.isModify = this.content !== this.textClip ? true : false
        // JSON
        // this.$emit('input', this.editor.getJSON())
      },
    })
  },
  beforeDestroy() {
    this.editor.destroy()
  },
  updated() {
    const codeBlockSelect = document.querySelector('select')
    if (codeBlockSelect && !this.isClipUploader) codeBlockSelect.style.pointerEvents = 'none'
  },
  methods: {
    // 스페이스바가 안 먹는 기현상을 해결하기 위한 핵
    spaceHack(e) {
      e.stopPropagation()
    },
    openDialog(type) {
      if (type === 'Undo') {
        this.isTexteditundoDialog = true
      }
    },
    closeDialog(val) {
      if (val === 'Undo') {
        this.isTexteditundoDialog = false
      }
    },
    setClipTextUndo() {
      this.editor.commands.setContent(this.content)
      this.isModify = false
    },
    undoResult(val) {
      if (val === 'Undo') {
        this.setClipTextUndo()
        this.isTexteditundoDialog = false
      }
    },
    async setClipTextUpdate() {
      if (this.isModify) {
        this.isSaveTextLoading = true
        let clipIndex = this.totalSectionList[this.sectionIndex].clipList.findIndex(
          (clip) => clip.clipId === this.currentClip.clipId
        )
        if (clipIndex >= 0) {
          this.totalSectionList[this.sectionIndex].clipList[clipIndex].textClip = this.textClip
          try {
            await setClipTextUpdate(
              this,
              this.currentMt['wsId'],
              this.currentMt['mtId'],
              this.currentClip['clipId'],
              this.textClip
            )
            // notify(this, 'Success', this.$t('updateSuccess'), 'success')
            const content = {
              component: Toast,
              props: {
                msg: this.$t('updateSuccess'),
                // submsg: '',
              },
            }
            this.$toast.success(content)
            this.isModify = false
          } catch (error) {
            // notify(this, 'Error', this.$t('general.error'), 'error')
            const content = {
              component: Toast,
              props: {
                msg: this.$t('general.error'),
                // submsg: '',
              },
            }
            this.$toast.error(content)
            console.log(error)
          } finally {
            this.isSaveTextLoading = false
          }
        }
      }
    },
  },
  computed: {
    ...mapGetters([
      'totalSectionList',
      'sectionIndex',
      'currentClip',
      'currentMt',
      'selectedWsInfo',
      'isMtOrganizer',
      'isWsOwner',
      'isWsManager',
    ]),
    ...mapState({
      userInfo: (state) => {
        return state.auth.userInfo && state.auth.userInfo !== null ? state.auth.userInfo : {}
      },
    }),
    content() {
      return this.$store.getters.currentClip.textClip
    },
    isClipUploader() {
      return (
        this.currentClip !== undefined &&
        this.currentClip !== null &&
        this.currentClip.clipUploader &&
        this.currentClip.clipUploader !== null &&
        this.userInfo &&
        this.userInfo !== null &&
        this.currentClip.clipUploader.userId === this.userInfo.userId
      )
    },
  },
  watch: {
    content(value) {
      // HTML
      const isSame = this.editor.getHTML() === value
      // JSON
      // const isSame = JSON.stringify(this.editor.getJSON()) === JSON.stringify(value)
      if (isSame) {
        return
      } else {
        this.editor.commands.setContent(value)
        this.textClip = this.editor.getHTML()
        this.isModify = false
      }
    },
  },
}
</script>

<style>
.ProseMirror {
  height: 100%;
}
.ProseMirror blockquote {
  padding-left: 1rem;
  margin-left: 1rem;
  border-left: 3px solid rgba(230, 230, 230, 0.1);
}
.code-block select {
  background-color: #31434f !important;
  color: #eee !important;
  outline: none !important;
  font-size: 12px !important;
  text-align: left;
  padding: 2px 10px !important;
}
.code-block pre code {
  font-size: 14px !important;
}
@import url('https://cdn.jsdelivr.net/npm/remixicon@2.5.0/fonts/remixicon.css');
.container-text {
  background: #31434f;
  overflow: auto;
  resize: vertical;
}
.content-text,
.content-avatar,
.content-name {
  color: #eee;
  font-weight: 500;
  text-align: center;
}
.content-text {
  color: #eee;
  font-size: 1.8rem;
}
.content-name {
  color: rgb(150, 167, 175);
  font-size: 1.2rem;
  vertical-align: middle;
}
.container-text {
  display: flex;
  background: #31434f;
  aspect-ratio: 16 / 9;
  align-items: center;
  justify-content: center;
  overflow-y: scroll;
  width: 100%;
}
.container-text > div:first-of-type {
  width: 100%;
  height: 100% !important;
  padding: 20px;
  background-color: #161d21;
  color: #eee;
  overflow: auto;
  line-height: 140%;
}
.container-text div:focus-visible {
  outline: none !important;
}

.tippy-box {
  background-color: transparent;
  padding: 0 !important;
}

.tippy-content {
  background-color: transparent;
  width: 100%;
  padding: 0 !important;
}

.tippy-content div {
  background-color: transparent;
  width: 100%;
  padding: 0 !important;
}

.tippy-content div.item {
  background-color: #eee;
  width: 100%;
  font-size: 14px;
  letter-spacing: -0.5px;
  padding: 2px 5px !important;
}

.tippy-content div button {
  background-color: #eee;
  border: 1px solid #eee;
  padding: 3px 5px 0px 5px;
}
.tippy-content div button i {
  font-size: 18px;
}
.tippy-content div button span {
  color: #232b2f;
  font-weight: normal;
  /* transition: all 0.3s; */
}
.tippy-content div button:hover {
  border: 1px solid #289067;
  background-color: #289067;
  color: #eee !important;
  opacity: 1;
}
.tippy-content div button:hover span {
  color: #eee !important;
}
.tippy-content div button.is-active {
  border: 1px solid #289067;
  background-color: #289067;
  opacity: 1;
}
.tippy-content div button.is-active i {
  color: #eee !important;
}

.ProseMirror > * + * {
  margin-top: 0.75em;
}
.ProseMirror pre {
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-family: 'JetBrainsMono', monospace;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}
.ProseMirror pre code {
  color: inherit;
  padding: 0;
  background: none;
  font-size: 0.8rem;
}
.ProseMirror pre .hljs-comment,
.ProseMirror pre .hljs-quote {
  color: #616161;
}
.ProseMirror pre .hljs-variable,
.ProseMirror pre .hljs-template-variable,
.ProseMirror pre .hljs-attribute,
.ProseMirror pre .hljs-tag,
.ProseMirror pre .hljs-name,
.ProseMirror pre .hljs-regexp,
.ProseMirror pre .hljs-link,
.ProseMirror pre .hljs-name,
.ProseMirror pre .hljs-selector-id,
.ProseMirror pre .hljs-selector-class {
  color: #f98181;
}
.ProseMirror pre .hljs-number,
.ProseMirror pre .hljs-meta,
.ProseMirror pre .hljs-built_in,
.ProseMirror pre .hljs-builtin-name,
.ProseMirror pre .hljs-literal,
.ProseMirror pre .hljs-type,
.ProseMirror pre .hljs-params {
  color: #fbbc88;
}
.ProseMirror pre .hljs-string,
.ProseMirror pre .hljs-symbol,
.ProseMirror pre .hljs-bullet {
  color: #b9f18d;
}
.ProseMirror pre .hljs-title,
.ProseMirror pre .hljs-section {
  color: #faf594;
}
.ProseMirror pre .hljs-keyword,
.ProseMirror pre .hljs-selector-tag {
  color: #70cff8;
}
.ProseMirror pre .hljs-emphasis {
  font-style: italic;
}
.ProseMirror pre .hljs-strong {
  font-weight: 700;
}
.ProseMirror h1 {
  margin-bottom: 0.75em;
}
.ProseMirror h2 {
  margin-bottom: 0.75em;
}
.ProseMirror h3 {
  margin-bottom: 0.75em;
}
.code-block select {
  color: #161d21;
  background-color: #eee;
  border-radius: 3px;
  padding: 2px 4px;
  font-size: 14px;
  letter-spacing: -0.5px;
  text-align: center;
}
.bubble-menu {
  overflow: hidden !important;
}
.bubble-menu button {
  border-radius: 5px;
  margin: 0 2px;
}
.bubble-button-color {
  position: relative;
  padding: 0 2px !important;
}
.bubble-button-color input {
  padding: 0 !important;
  width: 25px;
  height: 28.5px;
  cursor: pointer;
  top: 1px !important;
  left: 0px;
  position: relative;
}
.edit-btns {
  position: absolute;
  bottom: 15px;
  right: 20px;
  z-index: 100;
  width: auto;
  height: auto;
  background-color: transparent !important;
}
</style>
